import React, { useEffect, useState } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import { Plus, Edit2, Trash2, Save, X, Image as ImageIcon, GripVertical } from 'lucide-react';
import { Button } from '@/components/ui/button';
import AdminLayout from '@/components/admin/AdminLayout';
import { supabase } from '@/lib/customSupabaseClient';
import { useToast } from '@/components/ui/use-toast';

const AdminServices = () => {
  const { toast } = useToast();
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [editingService, setEditingService] = useState(null);

  // Drag and drop & ordering state
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const [savingOrder, setSavingOrder] = useState(false);

  // Form State
  const [formData, setFormData] = useState({
    title: '',
    title_fr: '',
    description: '',
    image_url: '',
    is_active: true,
    order_position: 0,
    pricing_plans: []
  });
  const [uploading, setUploading] = useState(false);

  const modules = {
    toolbar: [
      [{ 'header': [1, 2, 3, false] }],
      ['bold', 'italic', 'underline'],
      [{ 'list': 'ordered' }, { 'list': 'bullet' }],
      ['link', 'blockquote'],
      ['clean']
    ],
  };

  const formats = [
    'header', 'bold', 'italic', 'underline',
    'list', 'bullet', 'link', 'blockquote'
  ];

  useEffect(() => {
    fetchServices();
  }, []);

  const fetchServices = async () => {
    const { data, error } = await supabase
      .from('services')
      .select('*')
      .order('order_position', { ascending: true });

    if (!error && data) {
      const normalized = data.map((item, idx) => ({
        ...item,
        order_position: typeof item.order_position === 'number' && item.order_position > 0 ? item.order_position : idx + 1
      }));
      setServices(normalized);
    }
    setLoading(false);
  };

  // Persist updated positions to Supabase
  const saveNewOrder = async (updatedServices) => {
    setSavingOrder(true);
    try {
      const updates = updatedServices.map((service, idx) => {
        return supabase
          .from('services')
          .update({ order_position: idx + 1 })
          .eq('id', service.id);
      });

      const results = await Promise.all(updates);
      const hasError = results.some(r => r.error);
      if (hasError) throw new Error("Impossible de sauvegarder le nouvel ordre");

      toast({
        title: "Ordre mis à jour",
        description: "L'ordre des services a été actualisé automatiquement."
      });
    } catch (err) {
      toast({
        variant: "destructive",
        title: "Erreur",
        description: err.message
      });
      fetchServices();
    } finally {
      setSavingOrder(false);
    }
  };

  // Drag-and-drop handlers
  const handleDragStart = (e, index) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', `${index}`);
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (dragOverIndex !== index) {
      setDragOverIndex(index);
    }
  };

  const handleDrop = async (e, targetIndex) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === targetIndex) {
      setDraggedIndex(null);
      setDragOverIndex(null);
      return;
    }

    const updated = [...services];
    const [moved] = updated.splice(draggedIndex, 1);
    updated.splice(targetIndex, 0, moved);

    const reordered = updated.map((item, idx) => ({
      ...item,
      order_position: idx + 1
    }));

    setServices(reordered);
    setDraggedIndex(null);
    setDragOverIndex(null);

    await saveNewOrder(reordered);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const handleEdit = (service) => {
    setEditingService(service);
    setFormData({
      title: service.title,
      title_fr: service.title_fr,
      subtitle: service.subtitle,
      subtitle_fr: service.subtitle_fr,
      description: service.description,
      description_fr: service.description_fr,
      image_url: service.image_url,
      seo_keywords: service.seo_keywords,
      is_active: service.is_active,
      order_position: service.order_position,
      pricing_plans: service.pricing_plans || []
    });
    setIsEditing(true);
  };

  const handleCreate = () => {
    setEditingService(null);
    setFormData({
      title: '',
      title_fr: '',
      subtitle: '',
      subtitle_fr: '',
      description: '',
      description_fr: '',
      image_url: '',
      seo_keywords: '',
      is_active: true,
      order_position: services.length + 1,
      pricing_plans: []
    });
    setIsEditing(true);
  };

  const handleImageUpload = async (e) => {
    try {
      setUploading(true);
      const file = e.target.files[0];
      if (!file) return;

      const fileExt = file.name.split('.').pop();
      const fileName = `${Math.random()}.${fileExt}`;
      const filePath = `services/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('images')
        .upload(filePath, file);

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('images')
        .getPublicUrl(filePath);

      setFormData({ ...formData, image_url: publicUrl });
      toast({ title: "Image téléversée avec succès" });
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Erreur lors du téléversement de l'image",
        description: error.message
      });
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (editingService) {
        const { error } = await supabase
          .from('services')
          .update(formData)
          .eq('id', editingService.id);
        if (error) throw error;
        toast({ title: "Service mis à jour avec succès" });
      } else {
        const { error } = await supabase
          .from('services')
          .insert([formData]);
        if (error) throw error;
        toast({ title: "Service créé avec succès" });
      }
      setIsEditing(false);
      fetchServices();
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Erreur lors de l'enregistrement",
        description: error.message
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Êtes-vous sûr de vouloir supprimer ce service ?")) return;

    try {
      const { error } = await supabase
        .from('services')
        .delete()
        .eq('id', id);
      if (error) throw error;
      toast({ title: "Service supprimé avec succès" });
      fetchServices();
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Erreur lors de la suppression",
        description: error.message
      });
    }
  };

  const handlePricingPlanChange = (index, field, value) => {
    const newPlans = [...formData.pricing_plans];
    if (field === 'features') {
      newPlans[index][field] = value.split(',').map(s => s.trim());
    } else {
      newPlans[index][field] = value;
    }
    setFormData({ ...formData, pricing_plans: newPlans });
  };

  const addPricingPlan = () => {
    setFormData({
      ...formData,
      pricing_plans: [...formData.pricing_plans, { name: '', price: '', features: [], per: '' }]
    });
  };

  const removePricingPlan = (index) => {
    const newPlans = formData.pricing_plans.filter((_, i) => i !== index);
    setFormData({ ...formData, pricing_plans: newPlans });
  };



  return (
    <AdminLayout>
      <Helmet>
        <title>Services - Admin - Bigadsy</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      {isEditing ? (
        <section className="space-y-6">
          <div className="flex justify-between items-center mb-6 pb-2 border-b border-slate-200">
            <div>
              <h2 className="text-2xl font-bold text-slate-900">{editingService ? 'Modifier le service' : 'Nouveau service'}</h2>
              <p className="text-xs text-slate-500">Configurez les informations multilingues, le référencement SEO et les forfaits tarifaires</p>
            </div>
            <Button variant="outline" onClick={() => setIsEditing(false)} className="rounded-xl border-slate-200 text-slate-700 hover:bg-slate-100">
              <X className="h-4 w-4 mr-2" /> Annuler
            </Button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6 max-w-4xl bg-white p-6 sm:p-8 rounded-2xl border border-slate-200 shadow-xs">
            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1.5">Titre du service (EN)</label>
                  <input
                    type="text"
                    required
                    value={formData.title}
                    onChange={e => setFormData({ ...formData, title: e.target.value })}
                    className="w-full px-4 py-2.5 bg-white border border-slate-300 rounded-xl text-slate-900 focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 text-sm font-medium"
                    placeholder="Ex: Full-Stack Web Development"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1.5">Titre du service (FR)</label>
                  <input
                    type="text"
                    required
                    value={formData.title_fr}
                    onChange={e => setFormData({ ...formData, title_fr: e.target.value })}
                    className="w-full px-4 py-2.5 bg-white border border-slate-300 rounded-xl text-slate-900 focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 text-sm font-medium"
                    placeholder="Ex: Développement Web & Mobile"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1.5">Sous-titre du service (EN)</label>
                  <textarea
                    rows={3}
                    required
                    value={formData.subtitle}
                    onChange={e => setFormData({ ...formData, subtitle: e.target.value })}
                    className="w-full px-4 py-2.5 bg-white border border-slate-300 rounded-xl text-slate-900 focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1.5">Sous-titre du service (FR)</label>
                  <textarea
                    rows={3}
                    required
                    value={formData.subtitle_fr}
                    onChange={e => setFormData({ ...formData, subtitle_fr: e.target.value })}
                    className="w-full px-4 py-2.5 bg-white border border-slate-300 rounded-xl text-slate-900 focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1.5">Description détaillée (EN)</label>
                  <div className="bg-white border border-slate-300 rounded-xl overflow-hidden shadow-xs">
                    <ReactQuill
                      theme="snow"
                      value={formData.description}
                      onChange={(value) => setFormData({ ...formData, description: value })}
                      modules={modules}
                      formats={formats}
                      className="text-slate-900"
                      style={{ height: 'auto' }}
                    />
                  </div>
                  <label className="block text-sm font-semibold text-slate-700 mt-4 mb-1.5">Description détaillée (FR)</label>
                  <div className="bg-white border border-slate-300 rounded-xl overflow-hidden shadow-xs">
                    <ReactQuill
                      theme="snow"
                      value={formData.description_fr}
                      onChange={(value) => setFormData({ ...formData, description_fr: value })}
                      modules={modules}
                      formats={formats}
                      className="text-slate-900"
                      style={{ height: 'auto' }}
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mt-4 mb-1.5">Mots-clés SEO</label>
                  <textarea
                    rows={2}
                    required
                    value={formData.seo_keywords}
                    onChange={e => setFormData({ ...formData, seo_keywords: e.target.value })}
                    className="w-full px-4 py-2 bg-white border border-slate-300 rounded-xl text-slate-900 focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 text-sm"
                    placeholder="marketing, seo, react, ecommerce..."
                  />
                </div>

                <div className="flex items-center gap-4 pt-2">
                  <label className="flex items-center gap-2 text-slate-800 text-sm font-medium p-2.5 bg-slate-50 border border-slate-200 rounded-xl cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.is_active}
                      onChange={e => setFormData({ ...formData, is_active: e.target.checked })}
                      className="h-4 w-4 text-blue-600 rounded border-slate-300 focus:ring-blue-500"
                    />
                    Actif sur le site web
                  </label>
                  <div className="flex items-center gap-2">
                    <label className="text-slate-700 text-sm font-semibold">Ordre :</label>
                    <input
                      type="number"
                      value={formData.order_position}
                      onChange={e => setFormData({ ...formData, order_position: parseInt(e.target.value) || 0 })}
                      className="w-20 px-3 py-1.5 bg-white border border-slate-300 rounded-xl text-slate-900 text-sm font-semibold"
                    />
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <label className="block text-sm font-semibold text-slate-700 mb-1.5">Image de couverture du service</label>
                <div className="border-2 border-dashed border-slate-300 bg-slate-50/50 rounded-2xl p-6 text-center hover:bg-slate-100/40 transition-colors">
                  {formData.image_url ? (
                    <div className="relative">
                      <img src={formData.image_url} alt="Aperçu" className="h-44 w-full object-cover rounded-xl mb-4 border border-slate-200" />
                      <Button type="button" variant="destructive" size="sm" onClick={() => setFormData({ ...formData, image_url: '' })} className="absolute top-2 right-2 rounded-lg">Supprimer</Button>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center py-6">
                      <ImageIcon className="h-12 w-12 text-slate-300 mb-2" />
                      <p className="text-slate-500 text-sm mb-4">Téléversez une image de couverture</p>
                    </div>
                  )}
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    disabled={uploading}
                    className="block w-full text-xs text-slate-500 file:mr-4 file:py-2 file:px-4 file:rounded-xl file:border-0 file:text-xs file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 cursor-pointer"
                  />
                </div>
              </div>
            </div>

            <div className="border-t border-slate-100 pt-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-bold text-slate-900">Plans & Forfaits tarifaires</h3>
                <Button type="button" onClick={addPricingPlan} size="sm" variant="outline" className="border-slate-200 text-slate-700 hover:bg-slate-100 rounded-xl">
                  <Plus className="h-4 w-4 mr-1.5" /> Ajouter un forfait
                </Button>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                {formData.pricing_plans.map((plan, index) => (
                  <div key={index} className="bg-slate-50 p-4 rounded-xl border border-slate-200 relative">
                    <Button
                      type="button"
                      variant="destructive"
                      size="icon"
                      className="absolute top-2 right-2 h-6 w-6 rounded-md"
                      title="Supprimer le forfait"
                      onClick={() => removePricingPlan(index)}
                    >
                      <X className="h-3 w-3" />
                    </Button>
                    <div className="space-y-3">
                      <input
                        placeholder="Nom du forfait (ex: Basique / Pro)"
                        value={plan.name}
                        onChange={e => handlePricingPlanChange(index, 'name', e.target.value)}
                        className="w-full px-3 py-1.5 bg-white border border-slate-300 rounded-lg text-slate-900 text-sm focus:border-blue-500 font-medium"
                      />
                      <div className="flex gap-2">
                        <input
                          placeholder="Prix (ex: 2500)"
                          value={plan.price}
                          onChange={e => handlePricingPlanChange(index, 'price', e.target.value)}
                          className="w-full px-3 py-1.5 bg-white border border-slate-300 rounded-lg text-slate-900 text-sm focus:border-blue-500"
                        />
                        <input
                          placeholder="Périodicité (ex: par mois / par projet)"
                          value={plan.per || ''}
                          onChange={e => handlePricingPlanChange(index, 'per', e.target.value)}
                          className="w-full px-3 py-1.5 bg-white border border-slate-300 rounded-lg text-slate-900 text-sm focus:border-blue-500"
                        />
                      </div>
                      <textarea
                        placeholder="Fonctionnalités incluses (séparées par des virgules)"
                        value={Array.isArray(plan.features) ? plan.features.join(', ') : plan.features}
                        onChange={e => handlePricingPlanChange(index, 'features', e.target.value)}
                        className="w-full px-3 py-1.5 bg-white border border-slate-300 rounded-lg text-slate-900 text-sm h-20 focus:border-blue-500"
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex justify-end gap-3 pt-4 border-t border-slate-100">
              <Button type="button" variant="outline" onClick={() => setIsEditing(false)} className="rounded-xl border-slate-200 text-slate-700">Annuler</Button>
              <Button type="submit" disabled={loading} className="bg-gradient-to-r from-[#3B82F6] to-[#8B5CF6] hover:opacity-90 text-white font-semibold rounded-xl px-6 shadow-sm shadow-blue-500/20">
                {loading ? 'Enregistrement...' : 'Enregistrer le service'}
              </Button>
            </div>
          </form>
        </section>
      ) : (
        <div className="space-y-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-2 border-b border-slate-200">
            <div>
              <div className="flex items-center gap-3">
                <h1 className="text-3xl font-extrabold text-slate-900 tracking-tight">Services</h1>
                {savingOrder && (
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-blue-50 text-blue-600 border border-blue-200 animate-pulse">
                    Enregistrement de l'ordre...
                  </span>
                )}
              </div>
              <p className="text-sm text-slate-500 mt-1">
                Gérez vos prestations. Glissez-déposez les cartes pour modifier leur ordre d'affichage.
              </p>
            </div>
            <Button onClick={handleCreate} className="bg-gradient-to-r from-[#3B82F6] to-[#8B5CF6] hover:opacity-90 text-white font-semibold rounded-xl shadow-sm shadow-blue-500/20">
              <Plus className="mr-2 h-4 w-4" /> Ajouter un service
            </Button>
          </div>

          <div className="grid gap-3">
            {services.length === 0 ? (
              <div className="bg-white p-8 rounded-2xl border border-slate-200 text-center text-slate-400 text-sm">
                Aucun service trouvé. Créez votre première prestation.
              </div>
            ) : (
              services.map((service, index) => {
                const isBeingDragged = draggedIndex === index;
                const isDragOver = dragOverIndex === index && draggedIndex !== index;

                return (
                  <div
                    key={service.id}
                    draggable={true}
                    onDragStart={(e) => handleDragStart(e, index)}
                    onDragOver={(e) => handleDragOver(e, index)}
                    onDrop={(e) => handleDrop(e, index)}
                    onDragEnd={handleDragEnd}
                    className={`bg-white p-4 sm:p-5 rounded-2xl border transition-all flex items-center justify-between ${
                      isBeingDragged
                        ? 'opacity-30 border-blue-400 bg-blue-50/70 scale-[0.99] shadow-inner'
                        : isDragOver
                          ? 'border-blue-500 bg-blue-50/60 ring-2 ring-blue-500/30 shadow-md'
                          : 'border-slate-200/90 shadow-xs hover:shadow-md'
                    }`}
                  >
                    <div className="flex items-center gap-3 sm:gap-4 flex-1 min-w-0">
                      {/* Drag Handle */}
                      <div
                        className="cursor-grab active:cursor-grabbing text-slate-400 hover:text-blue-600 p-1.5 -ml-1 rounded-lg hover:bg-slate-100 select-none transition-colors"
                        title="Glisser pour réordonner"
                      >
                        <GripVertical className="h-5 w-5" />
                      </div>

                      {/* Image Thumbnail */}
                      {service.image_url ? (
                        <img
                          src={service.image_url}
                          alt={service.title_fr || service.title}
                          className="h-14 w-14 sm:h-16 sm:w-16 object-cover rounded-xl border border-slate-200 shrink-0 shadow-2xs"
                        />
                      ) : (
                        <div className="h-14 w-14 sm:h-16 sm:w-16 bg-slate-100 rounded-xl flex items-center justify-center shrink-0 border border-slate-200 text-slate-400">
                          <ImageIcon className="h-6 w-6" />
                        </div>
                      )}

                      {/* Title & Metadata */}
                      <div className="min-w-0 flex-1">
                        <h3 className="text-base sm:text-lg font-bold text-slate-900 truncate">
                          {service.title_fr || service.title}
                        </h3>

                        <div className="flex flex-wrap items-center gap-2.5 sm:gap-3 mt-1.5">
                          <span className={`text-[11px] font-bold px-2.5 py-0.5 rounded-full capitalize ${
                            service.is_active
                              ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                              : 'bg-rose-50 text-rose-700 border border-rose-200'
                          }`}>
                            {service.is_active ? 'Actif' : 'Inactif'}
                          </span>

                          <span className="text-[11px] font-mono font-bold text-slate-500 bg-slate-100 border border-slate-200/80 px-2 py-0.5 rounded-md">
                            #{service.order_position || index + 1}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Actions */}
                    <div
                      className="flex items-center gap-1 shrink-0 ml-3"
                      onMouseDown={(e) => e.stopPropagation()}
                      draggable={false}
                    >
                      <Button
                        variant="ghost"
                        size="icon"
                        title="Modifier"
                        onClick={() => handleEdit(service)}
                        className="h-9 w-9 text-blue-600 hover:bg-blue-50 rounded-xl"
                      >
                        <Edit2 className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        title="Supprimer"
                        onClick={() => handleDelete(service.id)}
                        className="h-9 w-9 text-rose-600 hover:bg-rose-50 rounded-xl"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default AdminServices;